import {
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAtomValue } from "jotai";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  TestSuiteRunRecordingData,
  TestSuiteRecordingApp,
  CompanyData,
} from "../../utils/types";
import RecordingVideoPlayer from "../../components/recording-video-player";
import RecordingInformation from "./recording_information";
import { LogDetailDialog } from "../../components/test-editor-gpt/execution-logs/logDetailDialog";
import { darkTheme } from "../../themes";
import styled from "@emotion/styled";
import { ThemeProvider } from "@mui/material/styles";
import {
  CircularProgress,
  Paper,
  styled as styledMaterial,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../auth/AuthContext";
import { companyDataAtom } from "../test-run/test-run-atoms";
import apiClient from "../../utils/api-client";

const TestRecordingPage = () => {
  const loaderData = useLoaderData() as TestSuiteRecordingApp;
  const {
    data: runRecordingData,
    isLoading: isLoadingTestRun,
    isError: isErrorTestRun,
    error: errorTestRun,
  } = useAtomValue(loaderData.testRunDataAtom);
  const { error: testSuiteError } = useAtomValue(loaderData.testSuiteDataAtom);
  const { user } = useContext<any>(AuthContext);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (testSuiteError != null) {
      if (testSuiteError.code === "permission-denied") {
        console.log("location", location);
        window.location.replace("/gpt-driver/tests");
      }
    }
  }, [testSuiteError]);

  // The failure-report iframe is cross-origin, so its "Watch recording" link
  // can't be intercepted directly. Instead it posts a message; on receiving it
  // we switch to the recording client-side (add ?live=1) — dismissing the
  // iframe and rendering the recording UI without a full page reload.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== "mobileboost:watch-recording") return;
      const params = new URLSearchParams(location.search);
      if (params.get("live") === "1") return;
      params.set("live", "1");
      void navigate({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [location.pathname, location.search, navigate]);

  const { data: suiteData } = useAtomValue(loaderData.testSuiteDataAtom);

  const {
    data: companyData,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    error: errorCompany,
  } = useAtomValue(
    companyDataAtom(user.isAnonymous ? undefined : suiteData?.organisationId)
  );

  const isLoading = isLoadingTestRun || isLoadingCompany;
  const isError = isErrorTestRun || isErrorCompany;
  const error = errorTestRun || errorCompany;

  // --- Failure-report page: when a confirmed app-bug run has a generated report
  // (run doc failureReport.status === "completed") and the URL isn't in ?live=1
  // mode, show the static report instead of the live recording. The report's own
  // "Watch recording" links return here with ?live=1 to render the recording.
  const { testSuiteId, runId } = useParams();
  const isLiveMode = new URLSearchParams(location.search).get("live") === "1";
  const lastRunData = runRecordingData?.runs?.at(-1)?.runData;
  const shouldShowReport =
    !isLiveMode && lastRunData?.failureReport?.status === "completed";

  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [reportFailed, setReportFailed] = useState(false);
  const organisationId = suiteData?.organisationId;

  useEffect(() => {
    if (!shouldShowReport || !testSuiteId || !runId || !organisationId) return;
    let cancelled = false;
    setReportUrl(null);
    setReportFailed(false);
    void (async () => {
      try {
        const { data } = await apiClient.get(
          `/suite/${testSuiteId}/run/${runId}/failure_report_url`,
          { params: { organisation_id: organisationId } }
        );
        if (!cancelled) setReportUrl(data?.url ?? null);
      } catch {
        // Report missing/expired — fall back to the live recording.
        if (!cancelled) setReportFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldShowReport, testSuiteId, runId, organisationId]);

  if (shouldShowReport && !reportFailed) {
    return (
      <ThemeProvider theme={darkTheme}>
        <ReportWrapper>
          {reportUrl ? (
            <ReportFrame src={reportUrl} title="Failure report" />
          ) : (
            <SpinnerWrapper>
              <CircularProgress />
            </SpinnerWrapper>
          )}
        </ReportWrapper>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <ContentWrapper>
        {!user.isAnonymous &&
          !isError &&
          !isLoading &&
          runRecordingData != null &&
          companyData != null && (
            <LogDetailDialogWrapper
              runRecordingData={runRecordingData}
              companyData={companyData}
            />
          )}
        <VideoSection>
          {isLoading && (
            <SpinnerWrapper>
              <CircularProgress />
            </SpinnerWrapper>
          )}
          {isError && (
            <Typography variant={"h6"} color={"error"}>
              Error: {error.message}
            </Typography>
          )}
          {!isError && !isLoading && runRecordingData != null && (
            <RecordingVideoPlayer
              videoUrl={runRecordingData.runs.at(-1)!.runData!.videoUrl}
              interactionLogs={runRecordingData.recording.interactionLogs ?? []}
              startedTimeInEpoch={
                runRecordingData.runs.at(-1)?.startedTimeInEpoch
              }
              finishedTimeInEpoch={
                runRecordingData.runs.at(-1)?.runData?.finishedTimeInEpoch
              }
            />
          )}
        </VideoSection>
        <InformationSection elevation={0} square>
          {runRecordingData != null && (
            <RecordingInformation
              runRecordingData={runRecordingData}
              companyData={companyData}
            />
          )}
        </InformationSection>
      </ContentWrapper>
    </ThemeProvider>
  );
};

const ContentWrapper = styled.div`
  display: grid;
  grid-template-areas: "video information";
  grid-template-columns: 30% 70%;
  height: 100%;
`;

const VideoSection = styled.div`
  grid-area: video;
`;

const InformationSection = styledMaterial(Paper)({
  gridArea: "information",
  overflow: "auto",
});

const SpinnerWrapper = styled.div`
  display: grid;
  place-content: center;
  height: 100%;
`;

const ReportWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const ReportFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const LogDetailDialogWrapper = ({
  runRecordingData,
  companyData,
}: {
  runRecordingData: TestSuiteRunRecordingData;
  companyData: CompanyData;
}) => {
  const loaderData = useLoaderData() as TestSuiteRecordingApp;
  const { data: suiteData } = useAtomValue(loaderData.testSuiteDataAtom);

  const parsedEnvVars = useMemo(
    () =>
      companyData?.settings?.envVars?.map((envVar) => `env.${envVar.key}`) ??
      [],
    [companyData?.settings?.envVars]
  );

  const acceptedDynamicValues = useMemo(
    () => [
      "timestamp",
      "currentDate",
      "currentDateFormatted",
      ...parsedEnvVars,
      ...(runRecordingData.runs
        ?.at(-1)
        ?.settings?.inputKeys?.map?.((inputKey) => inputKey.key) ?? []),
    ],
    [parsedEnvVars, runRecordingData.runs]
  );

  return (
    <>
      <ThemeProvider theme={darkTheme}>
        <LogDetailDialog
          acceptedDynamicValues={acceptedDynamicValues}
          companyData={companyData}
          platform={suiteData!.platform!}
          buildId={suiteData!.uploadId!}
          parentTestId={runRecordingData.runs.at(-1)!.testId!}
        />
      </ThemeProvider>
    </>
  );
};

export { TestRecordingPage };
